'use client';

import { useMemo, useState } from 'react';
import { calculatePanelCoverage, type PanelCoverageResult } from '@/lib/acousticsPhysics';
import type { LengthUnit } from '@/lib/tools/units';
import { SECOND_LINE } from '@/components/seo/roles';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import ToolMetricCard from '@/components/tools/ToolMetricCard';
import ToolLeadIn from '@/components/tools/ToolLeadIn';

interface WidgetProps {
  initialCoverage: PanelCoverageResult;
}

export default function AcousticPanelWidget(props: WidgetProps) {
  void props;
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(5.0);
  const [widthM, setWidthM] = useState(4.0);
  const [heightM, setHeightM] = useState(2.6);

  const coverage = useMemo(
    () => calculatePanelCoverage(lengthM, widthM, heightM),
    [lengthM, widthM, heightM],
  );

  const result = `${coverage.recommendedStudioPanelCount} panels for studio-standard 25% coverage across ${coverage.roomSurfaceAreaM2} m² of wall.`;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-6 border-b border-[var(--app-border)]">
        <ToolDimSlider label="Room length" valueM={lengthM} minM={2.5} maxM={12} stepM={0.1} unit={unit} onChangeM={setLengthM} />
        <ToolDimSlider label="Room width" valueM={widthM} minM={2} maxM={10} stepM={0.1} unit={unit} onChangeM={setWidthM} />
        <ToolDimSlider label="Room height" valueM={heightM} minM={2} maxM={5} stepM={0.1} unit={unit} onChangeM={setHeightM} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ToolMetricCard
          label="Light coverage (15%)"
          value={<>{coverage.recommendedLightPanelCount} <span className={SECOND_LINE}>panels</span></>}
          hint="2'x4' standard panels"
        />
        <ToolMetricCard
          label="Studio standard (25%)"
          value={<>{coverage.recommendedStudioPanelCount} <span className={SECOND_LINE}>panels</span></>}
          hint="Recommended for mixing rooms"
          emphasized
        />
        <ToolMetricCard
          label="Heavy coverage (35%)"
          value={<>{coverage.recommendedHeavyPanelCount} <span className={SECOND_LINE}>panels</span></>}
          hint="Vocal recording & live drums"
        />
      </div>

      <div className={`p-4 bg-[var(--app-bg)] border border-[var(--app-border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4 ${SECOND_LINE}`}>
        <div>
          <span className="font-semibold text-[var(--rt-ink)]">Total surface area:</span> {coverage.roomSurfaceAreaM2} m²
        </div>
        <div>
          <span className="font-semibold text-[var(--rt-ink)]">Bass trap corners:</span> 4 vertical corners
        </div>
        <div>
          <span className="font-semibold text-[var(--rt-ink)]">First reflections:</span> 6 panels min.
        </div>
      </div>

      <ToolLeadIn
        result={result}
        ctaLabel="Design panel layout in 3D"
        seed={{ w: widthM, l: lengthM, h: heightM, units: 'm' }}
      />
    </div>
  );
}
